import { useReducer, useCallback, useRef, useEffect } from 'react';

import { DRAFT_STATUS } from '../funds-step.constants';
import type { Fund, FundAward, FundCandidate } from '../funds-step.types';

type FundsState = {
  funds: Fund[];
  initialSnapshot: Fund[];
  deletedFundIds: Array<string | number>;
};

type FundsAction = { type: string; payload?: unknown };

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

const ACTIONS = {
  INIT: 'INIT',
  ADD_FUNDS: 'ADD_FUNDS',
  REMOVE_FUND: 'REMOVE_FUND',
  UPDATE_AWARDING_TYPE: 'UPDATE_AWARDING_TYPE',
  UPDATE_AWARD_AMOUNT: 'UPDATE_AWARD_AMOUNT',
  RESET: 'RESET',
};

const initialState: FundsState = {
  funds: [],
  initialSnapshot: [],
  deletedFundIds: [],
};

const getFundKey = (fund: Fund): string | number | undefined => fund.id || fund._tempId;

const fundsReducer = (state: FundsState, action: FundsAction): FundsState => {
  switch (action.type) {
    case ACTIONS.INIT: {
      const snapshot = deepClone(action.payload as Fund[]);

      return {
        funds: deepClone(action.payload as Fund[]).map((f: Fund) => ({
          ...f,
          _status: DRAFT_STATUS.CLEAN,
        })),
        initialSnapshot: snapshot,
        deletedFundIds: [],
      };
    }

    case ACTIONS.ADD_FUNDS: {
      const newFunds: Fund[] = (action.payload as FundCandidate[]).map(
        (clientFund) => ({
          _tempId: `new-${clientFund.id}-${Date.now()}`,
          _status: DRAFT_STATUS.NEW,
          specialFundId: clientFund.id,
          name: clientFund.name,
          awardingType: 'STACK',
          awards: (clientFund.awards || []).map((a: FundAward) => ({
            previousYearAmount: a.previousYearAmount,
            previousYearRecipients: a.previousYearRecipients,
            currentYearAmount: a.currentYearAmount ?? null,
          })),
        })
      );

      return { ...state, funds: [...state.funds, ...newFunds] };
    }

    case ACTIONS.REMOVE_FUND: {
      const fundKey = action.payload as string | number;
      const fund = state.funds.find((f: Fund) => getFundKey(f) === fundKey);

      if (!fund) return state;

      const nextFunds = state.funds.filter((f: Fund) => getFundKey(f) !== fundKey);

      if (fund._status === DRAFT_STATUS.NEW) {
        return { ...state, funds: nextFunds };
      }

      return {
        ...state,
        funds: nextFunds,
        deletedFundIds: [...state.deletedFundIds, fund.id as string | number],
      };
    }

    case ACTIONS.UPDATE_AWARDING_TYPE: {
      const { fundKey, value } = action.payload as { fundKey: string | number; value: string };

      return {
        ...state,
        funds: state.funds.map((f: Fund) => {
          if (getFundKey(f) !== fundKey) return f;

          return {
            ...f,
            awardingType: value,
            _status:
              f._status === DRAFT_STATUS.NEW
                ? DRAFT_STATUS.NEW
                : DRAFT_STATUS.MODIFIED,
          };
        }),
      };
    }

    case ACTIONS.UPDATE_AWARD_AMOUNT: {
      const { fundKey, awardIndex, value } = action.payload as {
        fundKey: string | number;
        awardIndex: number;
        value: unknown;
      };

      return {
        ...state,
        funds: state.funds.map((f: Fund) => {
          if (getFundKey(f) !== fundKey) return f;

          const awards = (f.awards ?? []).map((a: FundAward, i: number) =>
            i === awardIndex ? { ...a, currentYearAmount: value } : a
          );

          return {
            ...f,
            awards,
            _status:
              f._status === DRAFT_STATUS.NEW
                ? DRAFT_STATUS.NEW
                : DRAFT_STATUS.MODIFIED,
          };
        }),
      };
    }

    case ACTIONS.RESET:
      return fundsReducer(state, {
        type: ACTIONS.INIT,
        payload: state.initialSnapshot,
      });

    default:
      return state;
  }
};

export const useFundsDraft = (serverData: Fund[] | null | undefined) => {
  const [state, dispatch] = useReducer(fundsReducer, initialState);
  const initialized = useRef(false);

  useEffect(() => {
    if (serverData && !initialized.current) {
      dispatch({ type: ACTIONS.INIT, payload: serverData });
      initialized.current = true;
    }
  }, [serverData]);

  const addFunds = useCallback(
    (funds: FundCandidate[]) => dispatch({ type: ACTIONS.ADD_FUNDS, payload: funds }),
    []
  );

  const removeFund = useCallback(
    (fundKey: string | number) => dispatch({ type: ACTIONS.REMOVE_FUND, payload: fundKey }),
    []
  );

  const updateAwardingType = useCallback(
    (fundKey: string | number, value: string) =>
      dispatch({
        type: ACTIONS.UPDATE_AWARDING_TYPE,
        payload: { fundKey, value },
      }),
    []
  );

  const updateAwardAmount = useCallback(
    (fundKey: string | number, awardIndex: number, value: unknown) =>
      dispatch({
        type: ACTIONS.UPDATE_AWARD_AMOUNT,
        payload: { fundKey, awardIndex, value },
      }),
    []
  );

  const reset = useCallback(() => dispatch({ type: ACTIONS.RESET }), []);

  const markForReinit = useCallback(() => {
    initialized.current = false;
  }, []);

  const computeChanges = useCallback(() => {
    const mapAwards = (awards: FundAward[]) =>
      awards.map(({ previousYearAmount, previousYearRecipients, currentYearAmount }) => ({
        previousYearAmount,
        previousYearRecipients,
        currentYearAmount,
      }));

    const toCreate = state.funds
      .filter((f: Fund) => f._status === DRAFT_STATUS.NEW)
      .map(({ specialFundId, awardingType, awards }: Fund) => ({
        specialFundId,
        awardingType,
        awards: mapAwards(awards ?? []),
      }));

    const toUpdate = state.funds
      .filter((f: Fund) => f._status === DRAFT_STATUS.MODIFIED)
      .map(({ id, awardingType, awards }: Fund) => ({
        id,
        awardingType,
        awards: mapAwards(awards ?? []),
      }));

    const toDelete = [...state.deletedFundIds];

    return { toCreate, toUpdate, toDelete };
  }, [state]);

  const hasChanges =
    state.funds.some((f: Fund) => f._status !== DRAFT_STATUS.CLEAN) ||
    state.deletedFundIds.length > 0;

  const assignedFundIds: (string | number)[] = state.funds
    .map((f: Fund) => (f.specialFundId || f.id) as string | number | undefined)
    .filter((id): id is string | number => id != null);

  return {
    funds: state.funds,
    hasChanges,
    assignedFundIds,
    addFunds,
    removeFund,
    updateAwardingType,
    updateAwardAmount,
    reset,
    markForReinit,
    computeChanges,
  };
};
